import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, BarChart3, PieChart, Shield, ArrowLeft, Sparkles, Loader2, Lock, ChevronDown, X, Percent, Download, AlertTriangle, Newspaper, UserCheck, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, PieChart as RPieChart, Pie, Cell } from "recharts";
import type { InvestmentData } from "./InvestmentForm";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { getCurrencySymbol, getConversionRate } from "@/lib/currencies";
import { pushEvent } from "@/lib/datalayer";

interface ResultsDashboardProps {
  investmentData: InvestmentData;
  onBack: () => void;
  runsRemaining?: number | null;
  onRunAgain?: () => void;
  currency?: string;
}

interface NewsItem {
  headline: string;
  source: string;
  date: string;
  sentiment: string;
  summary: string;
}

interface StockROI {
  period: string;
  roi: number;
  value: number;
}

interface StockPick {
  ticker: string;
  name: string;
  sector: string;
  riskLevel: string;
  allocation: number;
  currentPrice: number;
  signal: string;
  confidence: number;
  reason: string;
  priceSource?: string;
  roi_projections?: StockROI[];
  latest_news?: NewsItem[];
}

interface ROIProjection {
  period: string;
  roi: number;
  value: number;
}

interface ProactiveAlert {
  event: string;
  date: string;
  impact: string;
  description: string;
  recommendation: string;
}

interface PersonalAdvice {
  recommended_risk_split: { low: number; medium: number; high: number };
  monthly_investment_suggestion: number;
  emergency_fund_status: string;
  diversification_advice: string;
  tax_considerations: string;
  retirement_readiness: string;
}

interface AnalysisResult {
  stocks: StockPick[];
  roi_projections: ROIProjection[];
  risk_score: number;
  market_sentiment: string;
  summary: string;
  plan: string;
  proactive_alerts?: ProactiveAlert[];
  personal_advice?: PersonalAdvice;
}

const COLORS = ["#E5A825", "#D4942A", "#C3832F", "#9B6A2F", "#7A5530", "#5D4225", "#E5C825", "#C8A830", "#B8962E", "#A8842C", "#986E2A", "#885A28", "#784828", "#683628", "#F0B830", "#D0A030"];

const CustomPieTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border border-border bg-card p-2 text-card-foreground shadow-card text-xs">
        <p className="font-display font-semibold">{payload[0].name}</p>
        <p>{payload[0].value}%</p>
      </div>
    );
  }
  return null;
};

const exportToPDF = async (result: AnalysisResult, stocks: StockPick[], investmentData: InvestmentData, t: (key: string) => string, currSymbol: string, convRate: number = 1) => {
  const cv = (v: number) => Math.round(v * convRate * 100) / 100;
  const { default: jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");

  const doc = new jsPDF();
  const gold = [229, 168, 37] as [number, number, number];
  const darkGray = [60, 60, 60] as [number, number, number];
  const lightGray = [150, 150, 150] as [number, number, number];
  const pageWidth = doc.internal.pageSize.getWidth();

  const addSectionTitle = (title: string, y: number) => {
    doc.setFontSize(14);
    doc.setTextColor(...gold);
    doc.text(title, 14, y);
    return y + 8;
  };

  const checkPageBreak = (y: number, needed: number = 30) => {
    if (y + needed > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      return 20;
    }
    return y;
  };

  // Title
  doc.setFontSize(22);
  doc.setTextColor(...gold);
  doc.text(t("pdf.title"), 14, 22);
  doc.setFontSize(10);
  doc.setTextColor(...lightGray);
  doc.text(`${investmentData.plan} ${t("pdf.plan")} | ${currSymbol}${investmentData.amount.toLocaleString()} | ${t("pdf.generated")} ${new Date().toLocaleDateString()}`, 14, 30);

  // Summary
  let yPos = 40;
  doc.setFontSize(11);
  doc.setTextColor(...darkGray);
  const summaryLines = doc.splitTextToSize(result.summary, 180);
  doc.text(summaryLines, 14, yPos);
  yPos += summaryLines.length * 5 + 8;

  // Stats row
  doc.setFontSize(10);
  doc.setTextColor(...gold);
  doc.text(`${t("pdf.riskScore")}: ${result.risk_score}/10  |  ${t("pdf.sentiment")}: ${result.market_sentiment}  |  ${t("pdf.stocks")}: ${stocks.length}  |  ${t("pdf.bestRoi")}: +${Math.max(...(result.roi_projections || []).map(r => r.roi)).toFixed(1)}%`, 14, yPos);
  yPos += 10;

  // Personal Advice (Pro)
  if (result.personal_advice) {
    yPos = checkPageBreak(yPos, 60);
    yPos = addSectionTitle(t("pdf.personalAdvice"), yPos);
    const pa = result.personal_advice;
    const adviceData = [
      [t("pdf.riskSplit"), `${t("results.lowRisk")} ${pa.recommended_risk_split.low}% | ${t("results.medRisk")} ${pa.recommended_risk_split.medium}% | ${t("results.highRisk")} ${pa.recommended_risk_split.high}%`],
      [t("pdf.monthlyInvestment"), `${currSymbol}${cv(pa.monthly_investment_suggestion || 0).toLocaleString()}/${t("pdf.mo")}`],
      [t("pdf.emergencyFund"), pa.emergency_fund_status?.replace(/_/g, " ")],
      [t("pdf.retirementReadiness"), pa.retirement_readiness || "N/A"],
    ];
    autoTable(doc, {
      startY: yPos,
      body: adviceData,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 50 } },
    });
    yPos = (doc as any).lastAutoTable.finalY + 4;

    if (pa.diversification_advice) {
      doc.setFontSize(9);
      doc.setTextColor(...darkGray);
      const divLines = doc.splitTextToSize(`${t("pdf.diversification")}: ${pa.diversification_advice}`, 180);
      yPos = checkPageBreak(yPos, divLines.length * 4 + 5);
      doc.text(divLines, 14, yPos);
      yPos += divLines.length * 4 + 3;
    }
    if (pa.tax_considerations) {
      const taxLines = doc.splitTextToSize(`${t("pdf.taxConsiderations")}: ${pa.tax_considerations}`, 180);
      yPos = checkPageBreak(yPos, taxLines.length * 4 + 5);
      doc.text(taxLines, 14, yPos);
      yPos += taxLines.length * 4 + 5;
    }
  }

  // Proactive Alerts
  if (result.proactive_alerts && result.proactive_alerts.length > 0) {
    yPos = checkPageBreak(yPos, 40);
    yPos = addSectionTitle(t("pdf.marketAlerts"), yPos);
    const alertData = [...result.proactive_alerts]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(a => [a.date, a.event, a.impact.toUpperCase(), a.description, a.recommendation]);
    autoTable(doc, {
      startY: yPos,
      head: [[t("pdf.date"), t("pdf.event"), t("pdf.impact"), t("pdf.description"), t("pdf.recommendation")]],
      body: alertData,
      theme: "grid",
      headStyles: { fillColor: gold, textColor: [255, 255, 255], fontSize: 8 },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 0: { cellWidth: 22 }, 2: { cellWidth: 16 } },
    });
    yPos = (doc as any).lastAutoTable.finalY + 8;
  }

  // ROI Projections
  if (result.roi_projections?.length) {
    yPos = checkPageBreak(yPos, 30);
    yPos = addSectionTitle(t("pdf.projectedRoi"), yPos);
    autoTable(doc, {
      startY: yPos,
      head: [[t("pdf.period"), t("pdf.roiPercent"), t("pdf.portfolioValue")]],
      body: result.roi_projections.map((r) => [r.period, `${r.roi >= 0 ? "+" : ""}${r.roi}%`, `${currSymbol}${cv(r.value).toLocaleString()}`]),
      theme: "grid",
      headStyles: { fillColor: gold, textColor: [255, 255, 255] },
      styles: { fontSize: 9 },
    });
    yPos = (doc as any).lastAutoTable.finalY + 8;
  }

  // Stocks table
  yPos = checkPageBreak(yPos, 30);
  yPos = addSectionTitle(t("pdf.stockRecommendations"), yPos);
  autoTable(doc, {
    startY: yPos,
    head: [[t("pdf.ticker"), t("pdf.name"), t("pdf.sector"), t("pdf.risk"), t("pdf.signal"), t("pdf.price"), t("pdf.allocPercent"), t("pdf.amount"), t("pdf.confidence")]],
    body: stocks.map((s) => [
      s.ticker, s.name, s.sector, s.riskLevel, s.signal || "-",
      `${currSymbol}${cv(s.currentPrice).toFixed(2)}`, `${s.allocation}%`,
      `${currSymbol}${Math.round(investmentData.amount * s.allocation / 100).toLocaleString()}`,
      s.confidence ? `${s.confidence}%` : "-"
    ]),
    theme: "grid",
    headStyles: { fillColor: gold, textColor: [255, 255, 255], fontSize: 8 },
    styles: { fontSize: 7 },
  });
  yPos = (doc as any).lastAutoTable.finalY + 8;

  // Per-stock details (reason, ROI, news)
  for (const stock of stocks) {
    yPos = checkPageBreak(yPos, 40);
    doc.setFontSize(11);
    doc.setTextColor(...gold);
    doc.text(`${stock.ticker}: ${stock.name}`, 14, yPos);
    yPos += 6;

    doc.setFontSize(9);
    doc.setTextColor(...darkGray);
    const reasonLines = doc.splitTextToSize(`${t("pdf.reason")}: ${stock.reason}`, 180);
    yPos = checkPageBreak(yPos, reasonLines.length * 4 + 5);
    doc.text(reasonLines, 14, yPos);
    yPos += reasonLines.length * 4 + 3;

    // Per-stock ROI
    if (stock.roi_projections && stock.roi_projections.length > 0) {
      yPos = checkPageBreak(yPos, 20);
      autoTable(doc, {
        startY: yPos,
        head: [stock.roi_projections.map(p => p.period)],
        body: [stock.roi_projections.map(p => `${p.roi >= 0 ? "+" : ""}${p.roi}% (${currSymbol}${cv(p.value || 0).toLocaleString()})`)],
        theme: "grid",
        headStyles: { fillColor: [180, 150, 50], textColor: [255, 255, 255], fontSize: 7 },
        styles: { fontSize: 7, cellPadding: 1.5 },
      });
      yPos = (doc as any).lastAutoTable.finalY + 4;
    }

    // News
    if (stock.latest_news && stock.latest_news.length > 0) {
      for (const news of stock.latest_news) {
        yPos = checkPageBreak(yPos, 15);
        doc.setFontSize(8);
        doc.setTextColor(...darkGray);
        const newsLine = doc.splitTextToSize(`[${news.sentiment?.toUpperCase()}] ${news.headline} | ${news.source} (${news.date})`, 180);
        doc.text(newsLine, 14, yPos);
        yPos += newsLine.length * 3.5 + 2;
      }
    }
    yPos += 4;
  }

  // Disclaimer
  yPos = checkPageBreak(yPos, 15);
  doc.setFontSize(8);
  doc.setTextColor(...lightGray);
  doc.text(t("pdf.disclaimer"), 14, yPos);

  doc.save(`StockAI_${investmentData.plan}_${new Date().toISOString().split("T")[0]}.pdf`);
};

const langLabel: Record<string, string> = { en: "English", ar: "العربية", ru: "Русский" };

const ResultsDashboard = ({ investmentData, onBack, runsRemaining, onRunAgain, currency = "USD" }: ResultsDashboardProps) => {
  const currSymbol = getCurrencySymbol(currency);
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const generatedLang = investmentData.language || "en";
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [stocks, setStocks] = useState<StockPick[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedStock, setExpandedStock] = useState<string | null>(null);
  const [removingStock, setRemovingStock] = useState<string | null>(null);
  const [reallocationRisk, setReallocationRisk] = useState({ low: 40, med: 35, high: 25 });
  const [convRate, setConvRate] = useState(1);

  // Fetch conversion rate from USD to user currency
  useEffect(() => {
    if (currency === "USD") { setConvRate(1); return; }
    getConversionRate("USD", currency).then(setConvRate);
  }, [currency]);

  const plan = investmentData.plan;
  const isBasic = plan === "Basic";
  const isPro = plan === "Pro";
  const isAdvanced = !isBasic;

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke("stock-advisor", {
          body: { ...investmentData },
        });
        if (fnError) throw fnError;
        setResult(data);
        setStocks(data.stocks || []);
      } catch (e: any) {
        console.error(e);
        setError("Failed to generate recommendations. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchRecommendations();
  }, []);

  const setReallocationValue = useCallback((which: "low" | "med" | "high", val: number) => {
    const clamped = Math.max(0, Math.min(100, val));
    setReallocationRisk((prev) => ({ ...prev, [which]: clamped }));
  }, []);

  const reallocationTotal = reallocationRisk.low + reallocationRisk.med + reallocationRisk.high;

  const confirmRemoveStock = (ticker: string) => {
    const stockToRemove = stocks.find((s) => s.ticker === ticker);
    if (!stockToRemove || stocks.length <= 1) return;

    const freedAllocation = stockToRemove.allocation;
    const remaining = stocks.filter((s) => s.ticker !== ticker);

    const totalRisk = reallocationRisk.low + reallocationRisk.med + reallocationRisk.high;
    const riskShares = {
      low: reallocationRisk.low / totalRisk,
      medium: reallocationRisk.med / totalRisk,
      high: reallocationRisk.high / totalRisk,
    };

    const byRisk: Record<string, StockPick[]> = { low: [], medium: [], high: [] };
    remaining.forEach((s) => byRisk[s.riskLevel]?.push(s));

    const updated = remaining.map((s) => {
      const riskGroup = byRisk[s.riskLevel] || [];
      if (riskGroup.length === 0) return s;
      const share = (riskShares[s.riskLevel as keyof typeof riskShares] || 0) * freedAllocation / riskGroup.length;
      return { ...s, allocation: Math.round((s.allocation + share) * 10) / 10 };
    });

    const total = updated.reduce((sum, s) => sum + s.allocation, 0);
    if (total > 0) {
      const factor = 100 / total;
      updated.forEach((s) => (s.allocation = Math.round(s.allocation * factor * 10) / 10));
      const diff = 100 - updated.reduce((sum, s) => sum + s.allocation, 0);
      if (updated.length > 0) updated[0].allocation = Math.round((updated[0].allocation + diff) * 10) / 10;
    }

    setStocks(updated);
    setRemovingStock(null);
    setExpandedStock(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-4" />
          <h3 className="font-display text-xl font-semibold mb-2">{t("results.analyzing")}</h3>
          <p className="text-muted-foreground text-sm">{t("results.analyzingDesc")}</p>
        </motion.div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-destructive mb-4">{error || t("results.error")}</p>
          <Button variant="gold-outline" onClick={onBack}>{t("results.tryAgain")}</Button>
        </div>
      </div>
    );
  }

  // Helper to convert USD values from API to user currency
  const cv = (usdValue: number) => Math.round(usdValue * convRate * 100) / 100;

  const chartData = (result.roi_projections || []).map((r) => ({
    name: r.period,
    value: cv(r.value),
    roi: r.roi,
  }));

  const pieData = stocks.map((s) => ({
    name: s.ticker,
    value: s.allocation,
  }));

  const bestROI = result.roi_projections?.length > 0
    ? Math.max(...result.roi_projections.map((r) => r.roi))
    : 0;

  return (
    <section className="py-6 sm:py-12 px-3 sm:px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
            <button onClick={onBack} className="text-muted-foreground text-sm hover:text-foreground transition-colors font-display">
              <ArrowLeft className="w-4 h-4 inline me-1" />
              {t("results.newAnalysis")}
            </button>
            <Button variant="gold-outline" size="sm" onClick={() => exportToPDF(result, stocks, investmentData, t, currSymbol, convRate)} className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              {t("results.exportPdf")}
            </Button>
          </div>

          {language !== generatedLang && (
            <div className="rounded-lg border border-gold/20 bg-secondary/50 p-3 mb-6 flex items-center gap-2 text-xs text-muted-foreground font-display">
              <AlertTriangle className="w-4 h-4 text-gold shrink-0" />
              {t("results.langMismatch").replace("{lang}", langLabel[generatedLang] || generatedLang)}
            </div>
          )}

          {/* Summary */}
          <div className="rounded-2xl border border-gold/20 bg-gradient-card p-4 sm:p-6 mb-8 shadow-gold">
            <div className="flex items-start gap-3 mb-4">
              <Sparkles className="w-5 h-5 text-gold mt-1 shrink-0" />
              <div>
                <h2 className="font-display text-xl sm:text-2xl font-bold mb-1">{t("results.yourPortfolio")}</h2>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  {plan} Plan • {currSymbol}{investmentData.amount.toLocaleString()} • {investmentData.riskAllocation.low}% Low / {investmentData.riskAllocation.medium}% Med / {investmentData.riskAllocation.high}% High Risk
                </p>
              </div>
            </div>
            <p className="text-sm text-secondary-foreground leading-relaxed">{result.summary}</p>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 mt-6">
              <div className="rounded-xl bg-secondary p-3 border border-border">
                <p className="text-xs text-muted-foreground font-display">{t("results.riskScore")}</p>
                <p className="text-2xl font-display font-bold text-gold">{result.risk_score}<span className="text-sm text-muted-foreground">/10</span></p>
              </div>
              <div className="rounded-xl bg-secondary p-3 border border-border">
                <p className="text-xs text-muted-foreground font-display">{t("results.stocks")}</p>
                <p className="text-2xl font-display font-bold">{stocks.length}</p>
              </div>
              {isAdvanced && (
                <div className="rounded-xl bg-secondary p-3 border border-border">
                  <p className="text-xs text-muted-foreground font-display">{t("results.sentiment")}</p>
                  <p className="text-lg font-display font-bold text-gold">{result.market_sentiment}</p>
                </div>
              )}
              <div className="rounded-xl bg-secondary p-3 border border-border">
                <p className="text-xs text-muted-foreground font-display">{t("results.bestROI")}</p>
                <p className="text-2xl font-display font-bold text-green-400">+{bestROI.toFixed(1)}%</p>
              </div>
            </div>
          </div>

          {/* Pro Personal Advice */}
          {isPro && result.personal_advice && (
            <div className="rounded-2xl border border-gold/20 bg-gradient-card p-4 sm:p-6 mb-8">
              <h3 className="font-display font-semibold flex items-center gap-2 mb-4">
                <UserCheck className="w-4 h-4 text-gold" />
                {t("results.personalAdvice")}
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold/10 border border-gold/20 text-gold">PRO</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="rounded-xl bg-secondary p-4 border border-border">
                  <p className="text-xs text-muted-foreground font-display mb-1">{t("results.recommendedRisk")}</p>
                  <div className="flex gap-3 text-sm font-display">
                    <span className="text-green-400">{result.personal_advice.recommended_risk_split.low}% Low</span>
                    <span className="text-gold">{result.personal_advice.recommended_risk_split.medium}% Med</span>
                    <span className="text-red-400">{result.personal_advice.recommended_risk_split.high}% High</span>
                  </div>
                </div>
                <div className="rounded-xl bg-secondary p-4 border border-border">
                  <p className="text-xs text-muted-foreground font-display mb-1">{t("results.monthlyInvestment")}</p>
                  <p className="text-lg font-display font-bold text-gold">{currSymbol}{cv(result.personal_advice.monthly_investment_suggestion || 0).toLocaleString()}/mo</p>
                </div>
                <div className="rounded-xl bg-secondary p-4 border border-border">
                  <p className="text-xs text-muted-foreground font-display mb-1">{t("results.emergencyFund")}</p>
                  <p className={`text-sm font-display font-semibold ${
                    result.personal_advice.emergency_fund_status === "adequate" ? "text-green-400" :
                    result.personal_advice.emergency_fund_status === "needs_attention" ? "text-gold" : "text-red-400"
                  }`}>{result.personal_advice.emergency_fund_status?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</p>
                </div>
                <div className="rounded-xl bg-secondary p-4 border border-border">
                  <p className="text-xs text-muted-foreground font-display mb-1">{t("results.retirementReadiness")}</p>
                  <p className="text-sm text-secondary-foreground">{result.personal_advice.retirement_readiness}</p>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                <div className="rounded-xl bg-secondary p-4 border border-border">
                  <p className="text-xs text-muted-foreground font-display mb-1">{t("results.diversificationAdvice")}</p>
                  <p className="text-sm text-secondary-foreground">{result.personal_advice.diversification_advice}</p>
                </div>
                <div className="rounded-xl bg-secondary p-4 border border-border">
                  <p className="text-xs text-muted-foreground font-display mb-1">{t("results.taxConsiderations")}</p>
                  <p className="text-sm text-secondary-foreground">{result.personal_advice.tax_considerations}</p>
                </div>
              </div>
            </div>
          )}

          {/* Proactive Alerts - Plus & Pro */}
          {isAdvanced && result.proactive_alerts && result.proactive_alerts.length > 0 && (
            <div className="rounded-2xl border border-gold/20 bg-gradient-card p-4 sm:p-6 mb-8">
              <h3 className="font-display font-semibold flex items-center gap-2 mb-4">
                <AlertTriangle className="w-4 h-4 text-gold" />
                {t("results.marketAlerts")}
              </h3>
              <div className="space-y-3">
                {[...result.proactive_alerts].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map((alert, i) => (
                  <div key={i} className="rounded-xl bg-secondary p-4 border border-border">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          alert.impact === "high" ? "bg-red-400" : alert.impact === "medium" ? "bg-gold" : "bg-green-400"
                        }`} />
                        <span className="font-display font-semibold text-sm">{alert.event}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground font-display">{alert.date}</span>
                    </div>
                    <p className="text-xs text-secondary-foreground mb-2">{alert.description}</p>
                    <div className="rounded-lg bg-card p-2 border border-border">
                      <p className="text-[11px] text-gold font-display">💡 {alert.recommendation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* ROI Chart */}
            <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-card">
              <h3 className="font-display font-semibold flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-gold" />
                {t("results.projectedROI")}
                {isBasic && <span className="text-[10px] text-muted-foreground ms-1">{t("results.3moOnly")}</span>}
                {plan === "Plus" && <span className="text-[10px] text-muted-foreground ms-1">{t("results.upTo2yr")}</span>}
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#E5A825" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#E5A825" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${currSymbol}${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: "hsl(220 18% 8%)", border: "1px solid hsl(220 15% 16%)", borderRadius: 12, fontSize: 12, color: "hsl(45 10% 90%)" }}
                    labelStyle={{ color: "hsl(45 10% 90%)" }}
                    itemStyle={{ color: "#E5A825" }}
                    formatter={(value: number) => [`${currSymbol}${value.toLocaleString()}`, t("results.portfolioValue")]}
                  />
                  <Area type="monotone" dataKey="value" stroke="#E5A825" fill="url(#goldGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Portfolio Allocation Pie */}
            <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-card">
              <h3 className="font-display font-semibold flex items-center gap-2 mb-4">
                <PieChart className="w-4 h-4 text-gold" />
                {t("results.allocation")}
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <RPieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" stroke="none">
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                </RPieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2">
                {pieData.map((d, i) => (
                  <span key={d.name} className="text-[10px] font-display flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    {d.name} {d.value}%
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Stock Recommendations */}
          <div className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-border">
              <h3 className="font-display font-semibold flex items-center gap-2 text-sm sm:text-base">
                <BarChart3 className="w-4 h-4 text-gold" />
                {t("results.stockRecs")}
              </h3>
            </div>

            {/* Desktop table - hidden on mobile */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                   <tr className="border-b border-border text-xs text-muted-foreground font-display">
                    <th className="px-6 py-3 text-start">{t("results.stock")}</th>
                    <th className="px-4 py-3 text-start">{t("results.sector")}</th>
                    <th className="px-4 py-3 text-center">{t("results.risk")}</th>
                    <th className="px-4 py-3 text-end">{t("results.price")}</th>
                    {isAdvanced && <th className="px-4 py-3 text-center">{t("results.signal")}</th>}
                    <th className="px-4 py-3 text-end">{t("results.allocation")}</th>
                    <th className="px-4 py-3 text-end">{t("results.amount")}</th>
                    {isPro && <th className="px-4 py-3 text-end">{t("results.confidence")}</th>}
                    <th className="px-4 py-3 text-center">{t("results.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {stocks.map((stock, i) => {
                    const investAmount = Math.round(investmentData.amount * stock.allocation / 100);
                    const isExpanded = expandedStock === stock.ticker;
                    const isRemoving = removingStock === stock.ticker;

                    return (
                      <React.Fragment key={stock.ticker}>
                        <motion.tr
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className={`border-b border-border/50 hover:bg-secondary/50 transition-colors cursor-pointer ${isExpanded ? "bg-secondary/30" : ""}`}
                          onClick={() => setExpandedStock(isExpanded ? null : stock.ticker)}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                              <div>
                                <span className="font-display font-semibold">{stock.ticker}</span>
                                <p className="text-xs text-muted-foreground">{stock.name}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-xs text-muted-foreground">{stock.sector}</td>
                          <td className="px-4 py-4 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-display font-medium ${
                              stock.riskLevel === "low" ? "bg-green-400/10 text-green-400" :
                              stock.riskLevel === "medium" ? "bg-gold/10 text-gold" :
                              "bg-red-400/10 text-red-400"
                            }`}>
                              {stock.riskLevel}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-end font-display text-sm">
                            {currSymbol}{cv(stock.currentPrice).toLocaleString()}
                            {stock.priceSource === "live" && <span className="ml-1 text-[9px] text-green-400">●</span>}
                          </td>
                          {isAdvanced && (
                            <td className="px-4 py-4 text-center">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-display font-medium bg-green-400/10 text-green-400">
                                <TrendingUp className="w-3 h-3" />
                                Buy
                              </span>
                            </td>
                          )}
                          <td className="px-4 py-4 text-end text-sm font-display">{stock.allocation}%</td>
                          <td className="px-4 py-4 text-end text-sm font-display text-gold">{currSymbol}{investAmount.toLocaleString()}</td>
                          {isPro && (
                            <td className="px-4 py-4 text-end">
                              <div className="w-12 h-1.5 bg-secondary rounded-full overflow-hidden ml-auto">
                                <div className="h-full bg-gradient-gold rounded-full" style={{ width: `${stock.confidence}%` }} />
                              </div>
                              <span className="text-[10px] text-muted-foreground">{stock.confidence}%</span>
                            </td>
                          )}
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={(e) => { e.stopPropagation(); setRemovingStock(stock.ticker); }}
                              className="text-muted-foreground hover:text-red-400 transition-colors p-1 rounded"
                              title={t("results.removeStock")}
                              disabled={stocks.length <= 1}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </motion.tr>

                        {/* Expanded Row */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.tr
                              key={`${stock.ticker}-expand`}
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                            >
                              <td colSpan={isAdvanced ? (isPro ? 9 : 8) : 7} className="px-6 py-4 bg-secondary/20 border-b border-border/50">
                                <div className="space-y-4">
                                  <div>
                                    <p className="text-xs font-display font-medium text-muted-foreground mb-1">{t("results.aiReasoning")}</p>
                                    <p className="text-sm text-secondary-foreground">{stock.reason}</p>
                                  </div>
                                  {isAdvanced && stock.roi_projections && stock.roi_projections.length > 0 && (
                                    <div>
                                      <p className="text-xs font-display font-medium text-muted-foreground mb-2">{t("results.projectedROIStock")}</p>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                                        {stock.roi_projections.map((proj) => (
                                          <div key={proj.period} className="rounded-lg bg-card border border-border p-2 text-center">
                                            <p className="text-[10px] text-muted-foreground font-display">{proj.period}</p>
                                            <p className={`text-sm font-display font-bold ${proj.roi >= 0 ? "text-green-400" : "text-red-400"}`}>
                                              {proj.roi >= 0 ? "+" : ""}{proj.roi}%
                                            </p>
                                            <p className="text-[10px] text-muted-foreground">{currSymbol}{cv(proj.value || 0).toLocaleString()}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {stock.latest_news && stock.latest_news.length > 0 && (
                                    <div>
                                      <p className="text-xs font-display font-medium text-muted-foreground mb-2 flex items-center gap-1">
                                        <Newspaper className="w-3.5 h-3.5" />
                                        {t("results.latestNews")}
                                      </p>
                                      <div className="space-y-2">
                                        {stock.latest_news.map((news, ni) => (
                                          <div key={ni} className="rounded-lg bg-card border border-border p-3">
                                            <div className="flex items-start justify-between gap-2">
                                              <p className="text-xs font-display font-medium text-foreground">{news.headline}</p>
                                              <span className={`shrink-0 w-2 h-2 rounded-full mt-1 ${
                                                news.sentiment === "positive" ? "bg-green-400" :
                                                news.sentiment === "negative" ? "bg-red-400" : "bg-muted-foreground"
                                              }`} />
                                            </div>
                                            <p className="text-[11px] text-muted-foreground mt-1">{news.summary}</p>
                                            <div className="flex items-center gap-2 mt-1.5">
                                              <span className="text-[10px] text-muted-foreground">{news.source}</span>
                                              <span className="text-[10px] text-muted-foreground">•</span>
                                              <span className="text-[10px] text-muted-foreground">{news.date}</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </motion.tr>
                          )}
                        </AnimatePresence>

                        {/* Remove confirmation */}
                        <AnimatePresence>
                          {isRemoving && (
                            <motion.tr
                              key={`${stock.ticker}-remove`}
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                            >
                              <td colSpan={isAdvanced ? (isPro ? 9 : 8) : 7} className="px-6 py-4 bg-red-400/5 border-b border-red-400/20">
                                <p className="text-sm font-display font-medium mb-3">
                                  {t("results.reallocate")} <span className="text-gold">{stock.allocation}%</span> {t("results.from")} {stock.ticker} {t("results.byRiskLevel")}
                                </p>
                                <div className="space-y-3 mb-3 max-w-md">
                                  {reallocationTotal !== 100 && (
                                    <div className={`text-xs font-display px-3 py-1.5 rounded-lg border ${
                                      reallocationTotal > 100 ? "border-red-400/30 bg-red-400/5 text-red-400" : "border-gold/30 bg-gold/5 text-gold"
                                    }`}>
                                      Total: {reallocationTotal}% | {reallocationTotal > 100 ? `reduce by ${reallocationTotal - 100}%` : `add ${100 - reallocationTotal}% more`}
                                    </div>
                                  )}
                                  {reallocationTotal === 100 && (
                                    <div className="text-xs font-display px-3 py-1.5 rounded-lg border border-green-400/30 bg-green-400/5 text-green-400">
                                      ✓ Total: 100%
                                    </div>
                                  )}
                                  <div className="grid grid-cols-3 gap-3">
                                  {[
                                    { label: t("form.lowRisk"), key: "low" as const, color: "text-green-400" },
                                    { label: t("form.medRisk"), key: "med" as const, color: "text-gold" },
                                    { label: t("form.highRisk"), key: "high" as const, color: "text-red-400" },
                                  ].map(({ label, key, color }) => (
                                    <div key={key}>
                                      <div className="flex justify-between items-center text-xs mb-1">
                                        <span className={`font-display ${color}`}>{label}</span>
                                        <input
                                          type="number"
                                          min={0}
                                          max={100}
                                          value={reallocationRisk[key]}
                                          onChange={(e) => setReallocationValue(key, parseInt(e.target.value) || 0)}
                                          className={`w-14 h-6 rounded bg-secondary border border-border px-1 text-center text-xs font-display font-bold ${color} focus:outline-none focus:ring-1 focus:ring-ring`}
                                        />
                                      </div>
                                      <input
                                        type="range"
                                        min={0}
                                        max={100}
                                        step={5}
                                        value={reallocationRisk[key]}
                                        onChange={(e) => setReallocationValue(key, parseInt(e.target.value) || 0)}
                                        className="w-full accent-primary cursor-pointer"
                                      />
                                    </div>
                                  ))}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button size="sm" variant="gold" onClick={() => confirmRemoveStock(stock.ticker)} disabled={reallocationTotal !== 100}>
                                    {t("results.confirmRemoval")}
                                  </Button>
                                  <Button size="sm" variant="gold-outline" onClick={() => setRemovingStock(null)}>
                                    {t("results.cancel")}
                                  </Button>
                                </div>
                              </td>
                            </motion.tr>
                          )}
                        </AnimatePresence>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card layout */}
            <div className="md:hidden divide-y divide-border">
              {stocks.map((stock, i) => {
                const investAmount = Math.round(investmentData.amount * stock.allocation / 100);
                const isExpanded = expandedStock === stock.ticker;
                const isRemoving = removingStock === stock.ticker;

                return (
                  <div key={stock.ticker}>
                    <button
                      className={`w-full p-4 text-left ${isExpanded ? "bg-secondary/30" : ""}`}
                      onClick={() => setExpandedStock(isExpanded ? null : stock.ticker)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                          <span className="font-display font-semibold">{stock.ticker}</span>
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-display font-medium ${
                            stock.riskLevel === "low" ? "bg-green-400/10 text-green-400" :
                            stock.riskLevel === "medium" ? "bg-gold/10 text-gold" :
                            "bg-red-400/10 text-red-400"
                          }`}>{stock.riskLevel}</span>
                          {isAdvanced && stock.signal && (
                            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-display font-medium bg-green-400/10 text-green-400">Buy</span>
                          )}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setRemovingStock(stock.ticker); }}
                          className="text-muted-foreground hover:text-red-400 transition-colors p-1 rounded"
                          disabled={stocks.length <= 1}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{stock.name} · {stock.sector}</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <p className="text-muted-foreground text-[10px]">{t("results.price")}</p>
                          <p className="font-display font-semibold">
                            {currSymbol}{cv(stock.currentPrice).toLocaleString()}
                            {stock.priceSource === "live" && <span className="ml-1 text-[9px] text-green-400">●</span>}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-[10px]">{t("results.allocation")}</p>
                          <p className="font-display font-semibold">{stock.allocation}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-[10px]">{t("results.amount")}</p>
                          <p className="font-display font-semibold text-gold">{currSymbol}{investAmount.toLocaleString()}</p>
                        </div>
                      </div>
                      {isPro && stock.confidence && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-gold rounded-full" style={{ width: `${stock.confidence}%` }} />
                          </div>
                          <span className="text-[10px] text-muted-foreground">{stock.confidence}%</span>
                        </div>
                      )}
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 space-y-3">
                            <div>
                              <p className="text-xs font-display font-medium text-muted-foreground mb-1">{t("results.aiReasoning")}</p>
                              <p className="text-sm text-secondary-foreground">{stock.reason}</p>
                            </div>
                            {isAdvanced && stock.roi_projections && stock.roi_projections.length > 0 && (
                              <div>
                                <p className="text-xs font-display font-medium text-muted-foreground mb-2">{t("results.projectedROIStock")}</p>
                                <div className="grid grid-cols-3 gap-2">
                                  {stock.roi_projections.map((proj) => (
                                    <div key={proj.period} className="rounded-lg bg-card border border-border p-2 text-center">
                                      <p className="text-[10px] text-muted-foreground font-display">{proj.period}</p>
                                      <p className={`text-sm font-display font-bold ${proj.roi >= 0 ? "text-green-400" : "text-red-400"}`}>
                                        {proj.roi >= 0 ? "+" : ""}{proj.roi}%
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {stock.latest_news && stock.latest_news.length > 0 && (
                              <div>
                                <p className="text-xs font-display font-medium text-muted-foreground mb-2 flex items-center gap-1">
                                  <Newspaper className="w-3.5 h-3.5" />
                                  {t("results.latestNews")}
                                </p>
                                <div className="space-y-2">
                                  {stock.latest_news.map((news, ni) => (
                                    <div key={ni} className="rounded-lg bg-card border border-border p-3">
                                      <p className="text-xs font-display font-medium text-foreground">{news.headline}</p>
                                      <p className="text-[11px] text-muted-foreground mt-1">{news.summary}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <AnimatePresence>
                      {isRemoving && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 bg-red-400/5 border-t border-red-400/20">
                            <p className="text-sm font-display font-medium py-3">
                              {t("results.reallocate")} <span className="text-gold">{stock.allocation}%</span> {t("results.from")} {stock.ticker}
                            </p>
                            <div className="space-y-3 mb-3">
                              {reallocationTotal !== 100 && (
                                <div className={`text-xs font-display px-3 py-1.5 rounded-lg border ${
                                  reallocationTotal > 100 ? "border-red-400/30 bg-red-400/5 text-red-400" : "border-gold/30 bg-gold/5 text-gold"
                                }`}>
                                  Total: {reallocationTotal}%
                                </div>
                              )}
                              <div className="grid grid-cols-3 gap-2">
                                {[
                                  { label: t("form.lowRisk"), key: "low" as const, color: "text-green-400" },
                                  { label: t("form.medRisk"), key: "med" as const, color: "text-gold" },
                                  { label: t("form.highRisk"), key: "high" as const, color: "text-red-400" },
                                ].map(({ label, key, color }) => (
                                  <div key={key}>
                                    <span className={`text-[10px] font-display ${color}`}>{label}</span>
                                    <input
                                      type="number" min={0} max={100}
                                      value={reallocationRisk[key]}
                                      onChange={(e) => setReallocationValue(key, parseInt(e.target.value) || 0)}
                                      className={`w-full h-7 rounded bg-secondary border border-border px-1 text-center text-xs font-display font-bold ${color} focus:outline-none`}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="gold" onClick={() => confirmRemoveStock(stock.ticker)} disabled={reallocationTotal !== 100}>
                                {t("results.confirmRemoval")}
                              </Button>
                              <Button size="sm" variant="gold-outline" onClick={() => setRemovingStock(null)}>
                                {t("results.cancel")}
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Live price legend */}
          <div className="mt-4 flex items-center gap-4 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="text-green-400">●</span> {t("results.livePrice")}</span>
            <span className="flex items-center gap-1"><span className="text-muted-foreground">●</span> {t("results.estimatedPrice")}</span>
          </div>

          <div className="mt-8 text-center">
            <div className="flex gap-3 justify-center flex-wrap">
              {runsRemaining != null && runsRemaining > 0 && onRunAgain ? (
                <Button variant="gold" size="lg" onClick={onRunAgain}>
                  {t("results.runAgain")} ({runsRemaining} {t("results.left")})
                </Button>
              ) : (
                <Button variant="gold" size="lg" onClick={onBack}>
                  {t("results.purchaseNew")}
                </Button>
              )}
              <Button variant="gold-outline" size="lg" onClick={() => {
                pushEvent({ event: "download_pdf", plan_type: investmentData.plan?.toLowerCase(), login_status: "logged_in", page_type: "home" });
                exportToPDF(result, stocks, investmentData, t, currSymbol, convRate);
              }}>
                <Download className="w-4 h-4 me-1" />
                {t("results.downloadPdf")}
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate("/history")}>
                <History className="w-4 h-4 me-1" />
                View History
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {t("results.disclaimer")}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ResultsDashboard;
